import React from 'react';
import './WhoWeWorkWith.css';
import { FaUsers, FaLightbulb, FaExclamationTriangle, FaHandshake, FaCheckCircle, FaTimesCircle, FaArrowRight } from 'react-icons/fa';

const WhoWeWorkWith = () => {
    return (
        <div className="who-we-work-with-page">
            {/* Page Header */}
            <section className="page-header">
                <div className="container" data-aos="fade-up">
                    <h1 className="who-hero-title">Who We <span className="text-outline-gradient">Work With</span></h1>
                    <h4 className="who-main-title">Finding the Right Fit for Meaningful Transformation</h4>
                    <p className="who-subtitle">Not every organization needs Triology and that’s intentional</p>

                    <div className="who-intro-grid mt-4">
                        <div className="who-intro-main">
                            <p>
                                Triology partners best with organizations that are serious about building clarity, alignment,
                                and execution discipline. Our work requires commitment, openness, and a willingness
                                to examine systems and leadership practices.
                            </p>
                        </div>

                        <div className="who-highlight-box">
                            <div className="highlight-line"></div>
                            <p>
                                This page is designed to help you determine whether Triology is the right partner for your organization.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Organizations We Partner With */}
            <section className="who-partners-section section">
                <div className="container">
                    <div className="section-header-centered" data-aos="fade-up">
                        <span className="services-section-label--small">ORGANIZATIONS WE PARTNER WITH</span>
                    </div>
                    <div className="engagement-dual-grid">
                        <div className="engagement-col" data-aos="fade-right">
                            <h2 className="engagement-title">Growth-stage organizations preparing for the next level</h2>
                            <div className="engagement-text">
                                <p>
                                    We primarily work with organizations that have moved beyond early-stage growth and are navigating increased complexity.
                                    These organizations typically have a strong core business but are beginning to experience strain across leadership bandwidth,
                                    systems, and execution.
                                </p>
                            </div>
                        </div>

                        <div className="engagement-col" data-aos="fade-left">
                            <div className="who-list-box">
                                <h3 className="list-box-title">Triology is particularly effective for:</h3>
                                <ul className="who-custom-list">
                                    <li><FaArrowRight /> <span>Scaling operations, teams, or geographies</span></li>
                                    <li><FaArrowRight /> <span>Transitioning from founder-driven to system-led execution</span></li>
                                    <li><FaArrowRight /> <span>Preparing for professionalization or enterprise-grade governance</span></li>
                                </ul>
                                <div className="engagement-highlight mt-4">
                                    <p>Our consulting helps these organizations stabilize, align, and scale with confidence.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Leadership Mindset */}
            <section className="who-mindset-section section bg-light">
                <div className="container">
                    <div className="section-header-centered" data-aos="fade-up">
                        <span className="services-section-label--small">LEADERSHIP MINDSET WE VALUE</span>
                    </div>
                    <div className="engagement-dual-grid reverse">
                        <div className="engagement-col" data-aos="fade-right">
                            <div className="who-card-accented">
                                <h3 className="accent-title">Leaders we partner with typically:</h3>
                                <ul className="who-bullet-list">
                                    <li>Are open to diagnosis and honest reflection</li>
                                    <li>Seek structured thinking and disciplined execution</li>
                                    <li>Are willing to take ownership of decisions and outcomes</li>
                                    <li>Value long-term capability over short-term relief</li>
                                </ul>
                            </div>
                        </div>

                        <div className="engagement-col" data-aos="fade-left">
                            <h2 className="engagement-title">Clarity-seeking, ownership-driven leadership</h2>
                            <div className="engagement-text">
                                <p>
                                    Successful Triology engagements depend heavily on leadership mindset. We work best with founders, CXOs,
                                    and leadership teams who value clarity over quick fixes and are willing to engage deeply with
                                    the organization’s realities.
                                </p>
                                <div className="engagement-highlight-text mt-3">
                                    <p>This mindset allows real transformation to take root.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Engagement Triggers */}
            <section className="who-triggers-section section">
                <div className="container">
                    <div className="section-header-centered" data-aos="fade-up">
                        <span className="services-section-label--small">TYPICAL ENGAGEMENT TRIGGERS</span>
                        <h2 className="engagement-title">When organizations usually reach out to us</h2>
                    </div>

                    <div className="triggers-grid mt-4">
                        {[
                            "Rapid growth followed by execution strain",
                            "Increasing leadership complexity and decision delays",
                            "Misalignment between strategy and operational reality",
                            "Stalled initiatives despite high effort",
                            "The need to professionalize systems and governance"
                        ].map((trigger, index) => (
                            <div className="trigger-card" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className="trigger-dot"></div>
                                <p>{trigger}</p>
                            </div>
                        ))}
                    </div>
                    <p className="triggers-footer mt-4" data-aos="fade-up">These moments, while challenging, are also opportunities for meaningful realignment.</p>
                </div>
            </section>

            {/* Not a Fit Section */}
            <section className="not-fit-section section">
                <div className="container">
                    <div className="not-fit-wrapper" data-aos="zoom-in">
                        <div className="not-fit-header">
                            <span className="label-red">WHEN TRIOLOGY MAY NOT BE THE RIGHT FIT</span>
                            <h2 className="engagement-title">Clarity matters on both sides</h2>
                        </div>

                        <div className="not-fit-content">
                            <p className="not-fit-intro">Triology may not be the right partner if an organization is:</p>
                            <div className="not-fit-grid">
                                <div className="not-fit-item">
                                    <FaTimesCircle className="icon-red" />
                                    <p>Looking for quick fixes without structural change</p>
                                </div>
                                <div className="not-fit-item">
                                    <FaTimesCircle className="icon-red" />
                                    <p>Seeking only advisory inputs without execution involvement</p>
                                </div>
                                <div className="not-fit-item">
                                    <FaTimesCircle className="icon-red" />
                                    <p>Unwilling to examine leadership alignment or governance</p>
                                </div>
                                <div className="not-fit-item">
                                    <FaTimesCircle className="icon-red" />
                                    <p>Expecting outsourced responsibility for outcomes</p>
                                </div>
                            </div>
                            <div className="not-fit-footer mt-4">
                                <p>Our work requires partnership and shared ownership.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What Clients Can Expect */}
            <section className="expectations-section section">
                <div className="container">
                    <div className="section-header-centered" data-aos="fade-up">
                        <span className="services-section-label--small">WHAT CLIENTS CAN EXPECT</span>
                        <h2 className="engagement-title">A consulting partnership, not a vendor relationship</h2>
                    </div>

                    <div className="expectations-content mt-4" data-aos="fade-up">
                        <div className="expectations-cards-row">
                            {[
                                "Thoughtful diagnosis before recommendations",
                                "Structured, context-specific system design",
                                "Disciplined execution governance through COSMOS",
                                "Close collaboration with leadership and core teams",
                                "Focus on outcomes and internal capability building"
                            ].map((item, index) => (
                                <div className="expectation-card" key={index}>
                                    <FaCheckCircle className="icon-green" />
                                    <p>{item}</p>
                                </div>
                            ))}
                        </div>
                        <p className="expect-footer center-text mt-4">We are selective because transformation deserves focus and integrity.</p>
                    </div>
                </div>
            </section>

            {/* Final Conclusion - Redesigned based on Image */}
            <section className="section section-bg-white conclusion-footer-section">
                <div className="container">
                    <div className="who-conclusion-card" data-aos="zoom-in">
                        <div className="who-conclusion-content">
                            <div className="who-conclusion-left">
                                {/* <span className="who-section-label--micro">CONCLUSION</span> */}
                                <h2 className="who-conclusion-title">The right partnership creates <br />the right outcomes</h2>
                                <p className="who-conclusion-text">
                                    If your organization is ready to pause, reflect, and build clarity before
                                    accelerating further, Triology can be a strong partner in your journey.
                                </p>
                                <div className="who-conclusion-statement">
                                    <span className="statement-line"></span>
                                    <p>We believe that the right work, done with the right intent and structure, creates lasting impact.</p>
                                </div>
                                <div className="mt-4">
                                    <a href="/contact" className="who-cta-btn-premium--small">Let's Discuss</a>
                                </div>
                            </div>

                            <div className="who-conclusion-right">
                                <div className="who-quote-box">
                                    <p>
                                        "Transformation is not just about changing plans;
                                        it's about changing the discipline with which those plans are executed."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WhoWeWorkWith;
